/* @moldura padrao */
/**
 * page.tsx (Minha Área → Aparência) — como o app se parece PARA MIM.
 *
 * ─── O MOLDE ───────────────────────────────────────────────────────────────
 * `adminux-account-setting.html` ("configurações em seções"): cards
 * `adminuiux-card` com `card-header` + `h6` e ícone, corpo em `row gx-3`,
 * página em `container py-3`.
 *
 * Custou quatro tentativas chegar aqui, e a causa foi sempre a mesma: eu não
 * abri o molde. A lente de design manda *"ache a página equivalente, abra o
 * `.html` e COPIE — não se inspire: copie"*, e a tabela do que nunca fazer traz
 * **"título de seção solto sobre o fundo"** com todas as letras.
 *
 * ─── LAYOUT: EMPILHADO, MENOS OS DOIS ÚLTIMOS ──────────────────────────────
 * Decisão dele, 28/08/2026: *"faz igual você tinha feito no início"* — e, logo
 * depois, *"cabeçalho e menu lateral podem ficar um do lado do outro"*.
 *
 * As tentativas de pôr TUDO lado a lado (meio a meio, depois 2/3 + 1/3)
 * encavalaram os cards: blocos com quantidades diferentes de amostras têm
 * alturas naturais diferentes, e um transborda sobre o vizinho.
 *
 * Cabeçalho e menu são a exceção porque o conteúdo é SIMÉTRICO — os dois têm
 * Formato e Preenchimento, com as grades cabendo numa linha cada. Medido depois
 * de montado: 209px de altura os dois, sem sobreposição. Cor e fundo continuam
 * inteiros; dezesseis amostras não cabem em meia tela.
 *
 * O que ficou das tentativas: o `card-header` com ícone, a célula de largura
 * fixa (que alinha as bolinhas de uma linha para a outra), o destaque em chip
 * e as três ações.
 *
 * ─── AS DUAS ABAS ──────────────────────────────────────────────────────────
 * Cortadas por FREQUÊNCIA: **Tema** é o que se troca porque cansou do azul;
 * **Marca** é o ajuste fino de quem está acertando a identidade, e é para lá
 * que vão a cor do ícone e os tamanhos de fonte.
 *
 * ─── SAIR SEM SALVAR DESFAZ ────────────────────────────────────────────────
 * *"Se eu seleciono o rosa e vou para o Início, ele continua rosa. Não faz
 * sentido nenhum."* Não fazia: o rascunho era gravado como se fosse decisão, e
 * o botão Salvar não queria dizer nada. Ver `descartarRascunho` no contexto.
 *
 * A PÁGINA INTEIRA É A PRÉVIA: o `ThemeContext` aplica no `<body>` na hora.
 * AS LISTAS vêm de `lib/aparencia`, a mesma fonte que o painel da paleta lê.
 */
import { useEffect, useState } from 'react';

import {
  CORES,
  ESTILOS_CABECALHO,
  ESTILOS_MENU,
  FUNDOS,
  LAYOUTS_CABECALHO,
  LAYOUTS_MENU,
  TAMANHOS_MARCA,
} from '@/lib/aparencia';
import { CustomMarcaCasaANome } from '@/components/CustomMarcasCasaA';
import { useTheme } from '@/contexts/ThemeContext';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

type Aba = 'tema' | 'marca';

const ABAS: { id: Aba; nome: string; icone: string }[] = [
  { id: 'tema', nome: 'Tema', icone: 'bi-palette' },
  { id: 'marca', nome: 'Marca', icone: 'bi-rulers' },
];

/**
 * Uma célula de escolha — amostra em cima, nome embaixo, largura FIXA.
 *
 * A largura fixa é o que mantém as bolinhas alinhadas de uma linha para a
 * outra: antes cada célula media o próprio rótulo, e "Vermelho" empurrava a
 * fila inteira. O nome completo vai no `title`, para quando o rótulo truncar.
 */
function Escolha({
  ativo,
  nome,
  aoEscolher,
  larga,
  children,
}: {
  ativo: boolean;
  nome: string;
  aoEscolher: () => void;
  /** Célula bem mais larga — para a grade da marca. Ver `casaa-escolha-larga`. */
  larga?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="col-auto">
      <div
        className={`casaa-escolha text-center ${larga ? 'casaa-escolha-larga' : ''} ${
          ativo ? 'active' : ''
        }`}
        onClick={aoEscolher}
        title={nome}
        role="button"
      >
        {children}
        <p className="casaa-escolha-nome">{nome}</p>
      </div>
    </div>
  );
}

/** O cabeçalho de um card, no molde: `h6` com ícone à esquerda. */
function Cabecalho({ icone, titulo, apoio }: { icone: string; titulo: string; apoio?: string }) {
  return (
    <div className="card-header bg-transparent">
      <h6 className="mb-0">
        <i className={`bi ${icone} me-2`}></i>
        {titulo}
      </h6>
      {apoio && <p className="text-secondary small mb-0 mt-1">{apoio}</p>}
    </div>
  );
}

export function AparenciaPage() {
  const [aba, setAba] = useState<Aba>('tema');
  const {
    colorTheme,
    setColorTheme,
    darkMode,
    setDarkMode,
    backgroundGradient,
    setBackgroundGradient,
    headerLayout,
    setHeaderLayout,
    headerStyle,
    setHeaderStyle,
    sidebarLayout,
    setSidebarLayout,
    sidebarStyle,
    setSidebarStyle,
    tamanhoMarca,
    setTamanhoMarca,
    salvar,
    desfazer,
    voltarAoPadrao,
    descartarRascunho,
    temAlteracaoNaoSalva,
  } = useTheme();

  /* Sair da tela sem salvar desfaz. O `descartarRascunho` lê a última salva na
     hora — não a que estava aqui quando a tela montou —, senão salvar e depois
     sair desfaria o que acabou de ser salvo. */
  useEffect(() => descartarRascunho, [descartarRascunho]);

  return (
    <CustomMolduraPagina
      titulo="Aparência"
      descricao={
        temAlteracaoNaoSalva
          ? 'Você está experimentando. Salve para manter — sair daqui desfaz.'
          : 'Como o sistema se parece para você. Fica guardado neste aparelho.'
      }
      acoes={
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-sm btn-link text-secondary"
              onClick={voltarAoPadrao}
              title="Volta à combinação de fábrica da Casa A"
            >
              <i className="bi bi-arrow-counterclockwise me-1"></i>Padrão
            </button>
            {temAlteracaoNaoSalva && (
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={desfazer}>
                Desfazer
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm btn-theme"
              onClick={salvar}
              disabled={!temAlteracaoNaoSalva}
            >
              <i className="bi bi-check-lg me-1"></i>Salvar
            </button>
          </div>
      }
    >

      <ul className="nav nav-tabs adminuiux-tabs mb-3 mb-lg-4">
        {ABAS.map((a) => (
          <li className="nav-item" key={a.id}>
            <button
              type="button"
              className={`nav-link ${aba === a.id ? 'active' : ''}`}
              onClick={() => setAba(a.id)}
            >
              <i className={`bi ${a.icone} me-2`}></i>
              {a.nome}
            </button>
          </li>
        ))}
      </ul>

      {aba === 'tema' && (
        <>
          <div className="card adminuiux-card mb-3 mb-lg-4">
            <Cabecalho
              icone="bi-palette"
              titulo="Cor do tema"
              apoio="Pinta os botões, os links, os ícones e o que estiver em foco."
            />
            <div className="card-body">
              <div className="row gx-2 gy-2">
                {CORES.map((c) => (
                  <Escolha
                    key={c.id}
                    nome={c.nome}
                    ativo={colorTheme === c.id}
                    aoEscolher={() => setColorTheme(c.id)}
                  >
                    {/* Mesmo caminho do painel da paleta: `bg-theme-1` sob
                        `theme-{id}`. O "Padrão" pinta o roxo de fábrica porque
                        `theme-default` passou a existir em `estilos/`. */}
                    <span
                      className={`avatar avatar-40 rounded-circle mb-2 casaa-escolha-amostra bg-theme-1 theme-${c.id}`}
                    ></span>
                  </Escolha>
                ))}
              </div>
            </div>
          </div>

          <div className="card adminuiux-card mb-3 mb-lg-4">
            <Cabecalho
              icone="bi-image"
              titulo="Plano de fundo"
              apoio="Trocar o fundo não troca a cor do tema — são escolhas separadas."
            />
            <div className="card-body">
              <div className="row gx-2 gy-2">
                {FUNDOS.map((f) => (
                  <Escolha
                    key={f.id}
                    nome={f.nome}
                    ativo={backgroundGradient === f.id}
                    aoEscolher={() => setBackgroundGradient(f.id)}
                  >
                    <span
                      className={`avatar avatar-40 rounded-circle mb-2 casaa-escolha-amostra ${f.amostra}`}
                    >
                      {f.id === 'bg-default' && <i className="bi bi-slash-circle"></i>}
                    </span>
                  </Escolha>
                ))}
              </div>
            </div>
          </div>

          <div className="card adminuiux-card mb-3 mb-lg-4">
            <Cabecalho
              icone="bi-circle-half"
              titulo="Claro ou escuro"
              apoio="O escuro cansa menos a vista à noite e na obra."
            />
            <div className="card-body">
              <div className="row align-items-center gx-3">
                <div className="col-auto">Claro</div>
                <div className="col-auto px-0">
                  <div className="form-check form-switch sunmoon">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="pref-dark"
                      checked={darkMode}
                      onChange={(e) => setDarkMode(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="pref-dark">
                      <i className="bi bi-brightness-low sun"></i>
                      <i className="bi bi-moon-stars moon"></i>
                    </label>
                  </div>
                </div>
                <div className="col-auto">Escuro</div>
              </div>
            </div>
          </div>

          {/* CABEÇALHO E MENU LADO A LADO — pedido dele, 28/08: *"são dois
              painéis, um em cima do outro; pode colocar um do lado do outro,
              fica mais razoável"*. Estava ocupando a extensão toda para mostrar
              duas fileiras curtas de amostras.

              Estes dois podem dividir a linha sem o encavalamento que derrubou
              a versão anterior porque o conteúdo é SIMÉTRICO: os dois têm
              Formato e Preenchimento, e as duas grades cabem numa linha cada.
              Alturas naturalmente iguais — `h-100` só acerta o arremate. Os
              blocos de cima continuam inteiros: dezesseis cores e treze fundos
              não cabem em meia tela. */}
          <div className="row gx-3 gx-xl-4">
            <div className="col-12 col-lg-6">
          <div className="card adminuiux-card mb-3 mb-lg-4 h-100">
            <Cabecalho icone="bi-window" titulo="Cabeçalho" apoio="A barra do topo." />
            <div className="card-body">
              <p className="text-secondary fs-12 mb-2">Formato</p>
              <div className="row gx-2 gy-2 mb-3">
                {LAYOUTS_CABECALHO.map((l) => (
                  <Escolha
                    key={l.id}
                    nome={l.nome}
                    ativo={headerLayout === l.id}
                    aoEscolher={() => setHeaderLayout(l.id)}
                  >
                    <span className="avatar avatar-40 rounded mb-2 bg-theme-1-subtle text-theme-1">
                      <i className={l.icone}></i>
                    </span>
                  </Escolha>
                ))}
              </div>
              <p className="text-secondary fs-12 mb-2">Preenchimento</p>
              <div className="row gx-2 gy-2">
                {ESTILOS_CABECALHO.map((e) => (
                  <Escolha
                    key={e.id}
                    nome={e.nome}
                    ativo={headerStyle === e.id}
                    aoEscolher={() => setHeaderStyle(e.id)}
                  >
                    <span
                      className={`avatar avatar-40 rounded mb-2 casaa-escolha-amostra ${e.amostra}`}
                    ></span>
                  </Escolha>
                ))}
              </div>
            </div>
          </div>

            </div>
            <div className="col-12 col-lg-6">
          <div className="card adminuiux-card mb-3 mb-lg-4 h-100">
            <Cabecalho
              icone="bi-layout-sidebar"
              titulo="Menu lateral"
              apoio="Em «Só ícones» o menu encolhe e sobra tela."
            />
            <div className="card-body">
              <p className="text-secondary fs-12 mb-2">Formato</p>
              <div className="row gx-2 gy-2 mb-3">
                {LAYOUTS_MENU.map((l) => (
                  <Escolha
                    key={l.id}
                    nome={l.nome}
                    ativo={sidebarLayout === l.id}
                    aoEscolher={() => setSidebarLayout(l.id)}
                  >
                    {/* O par de contraste: aqui era o `accent`, que no modo
                        escuro é `rgb(3,4,94)` sobre a MESMA cor a 25% — os
                        ícones sumiam. Medido em 28/08. */}
                    <span className="avatar avatar-40 rounded mb-2 bg-theme-1-subtle text-theme-1">
                      <i className={l.icone}></i>
                    </span>
                  </Escolha>
                ))}
              </div>
              <p className="text-secondary fs-12 mb-2">Preenchimento</p>
              <div className="row gx-2 gy-2">
                {ESTILOS_MENU.map((e) => (
                  <Escolha
                    key={e.id}
                    nome={e.nome}
                    ativo={sidebarStyle === e.id}
                    aoEscolher={() => setSidebarStyle(e.id)}
                  >
                    <span
                      className={`avatar avatar-40 rounded mb-2 casaa-escolha-amostra ${e.amostra}`}
                    ></span>
                  </Escolha>
                ))}
              </div>
            </div>
          </div>
            </div>
          </div>
        </>
      )}

      {aba === 'marca' && (
        <div className="card adminuiux-card mb-3 mb-lg-4">
          <Cabecalho
            icone="bi-rulers"
            titulo="Tamanho da marca"
            apoio="A altura do nome CASAA ao lado do símbolo, no topo da tela."
          />
          <div className="card-body">
            <div className="row gx-2 gy-2">
              {TAMANHOS_MARCA.map((t) => (
                <Escolha
                  key={t.px}
                  nome={`${t.nome} · ${t.px}px`}
                  ativo={tamanhoMarca === t.px}
                  aoEscolher={() => setTamanhoMarca(t.px)}
                  larga
                >
                  {/* A AMOSTRA É A PRÓPRIA MARCA, na altura da opção. A caixa
                      tem a altura da maior para as células não pularem — e a
                      célula é larga o bastante para o `flex` não ENCOLHER o
                      SVG, que era o que fazia todas parecerem do mesmo tamanho:
                      36px de altura dão 159px de largura, e a célula tinha 104. */}
                  <span
                    className="d-flex align-items-center justify-content-center mb-2"
                    style={{ height: '40px' }}
                  >
                    <CustomMarcaCasaANome altura={t.px} />
                  </span>
                </Escolha>
              ))}
            </div>
            <p className="small text-secondary mb-0 mt-2">
              Aqui também vão entrar a cor do ícone da marca e o tamanho da fonte
              e do ícone do menu.
            </p>
          </div>
        </div>
      )}
    </CustomMolduraPagina>
  );
}
